import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { Page } from '../types';

/**
 * Listagem paginada no servidor — 10 por página, o tamanho padrão do backend.
 *
 * `chave` é a queryKey SEM a página, com os filtros dentro: quando ela muda (outro
 * filtro, outra busca), a lista volta sozinha para a primeira página. A página entra
 * no fim da queryKey e a anterior fica na tela enquanto a próxima carrega. As
 * invalidações por domínio (`invalidando(CHAVES_X.dominio)`) pegam todas as páginas.
 */
export default function usePagina<T>(
  chave: readonly unknown[],
  buscar: (page: number) => Promise<AxiosResponse<Page<T>>>,
  enabled = true,
) {
  const id = JSON.stringify(chave);
  const [estado, setEstado] = useState({ id, pagina: 0 });
  const pagina = estado.id === id ? estado.pagina : 0;

  const query = useQuery({
    queryKey: [...chave, pagina],
    queryFn: async () => (await buscar(pagina)).data,
    placeholderData: keepPreviousData,
    enabled,
  });

  const totalPaginas = query.data?.totalPages ?? 0;

  // Excluiu o último item da última página: volta para a última que ainda existe.
  useEffect(() => {
    if (query.data && !query.isPlaceholderData && pagina > 0 && pagina >= totalPaginas) {
      setEstado({ id, pagina: Math.max(0, totalPaginas - 1) });
    }
  }, [query.data, query.isPlaceholderData, pagina, totalPaginas, id]);

  return {
    itens: query.data?.content ?? [],
    total: query.data?.totalElements ?? 0,
    totalPaginas,
    pagina,
    setPagina: (nova: number) => setEstado({ id, pagina: nova }),
    query,
  };
}
